'use client'

import * as React from 'react'
import { AlertCircleIcon, LoaderCircleIcon } from 'lucide-react'

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { cn } from '@/lib/utils'

const formDialogWidths = {
  sm: 'sm:max-w-md',
  default: 'sm:max-w-lg',
  lg: 'sm:max-w-2xl',
} as const

type FormDialogContentProps = Omit<
  React.ComponentProps<typeof DialogContent>,
  'children' | 'title'
> & {
  title: React.ReactNode
  description: React.ReactNode
  children: React.ReactNode
  submitLabel: string
  onSubmit: React.FormEventHandler<HTMLFormElement>
  busy?: boolean
  busyLabel?: string
  cancelLabel?: string
  error?: React.ReactNode
  errorTitle?: string
  footer?: React.ReactNode
  size?: keyof typeof formDialogWidths
  bodyClassName?: string
}

function FormDialogContent({
  title,
  description,
  children,
  submitLabel,
  onSubmit,
  busy = false,
  busyLabel = 'Saving changes',
  cancelLabel = 'Cancel',
  error,
  errorTitle = 'Unable to save',
  footer,
  size = 'default',
  bodyClassName,
  className,
  showCloseButton = true,
  onOpenAutoFocus,
  onEscapeKeyDown,
  onPointerDownOutside,
  ...props
}: FormDialogContentProps) {
  const formRef = React.useRef<HTMLFormElement>(null)

  return (
    <DialogContent
      className={cn(
        'max-h-[calc(100dvh-(var(--dialog-gutter)*2))] max-w-[calc(100vw-(var(--dialog-gutter)*2))] gap-0 overflow-hidden p-0',
        formDialogWidths[size],
        className,
      )}
      showCloseButton={showCloseButton && !busy}
      onOpenAutoFocus={(event) => {
        onOpenAutoFocus?.(event)
        if (event.defaultPrevented) return

        const focusTarget = formRef.current?.querySelector<HTMLElement>(
          "[data-autofocus]:not(:disabled), input:not([type='hidden']):not(:disabled), textarea:not(:disabled), select:not(:disabled), [role='combobox']:not([aria-disabled='true']), button:not(:disabled):not([data-slot='field-help'])",
        )
        if (focusTarget) {
          event.preventDefault()
          focusTarget.focus()
        }
      }}
      onEscapeKeyDown={(event) => {
        onEscapeKeyDown?.(event)
        if (busy && !event.defaultPrevented) event.preventDefault()
      }}
      onPointerDownOutside={(event) => {
        onPointerDownOutside?.(event)
        if (busy && !event.defaultPrevented) event.preventDefault()
      }}
      {...props}
    >
      <form
        ref={formRef}
        className="flex min-h-0 max-h-[calc(100dvh-(var(--dialog-gutter)*2))] flex-col"
        aria-busy={busy}
        data-slot="form-dialog"
        onSubmit={(event) => {
          if (busy) {
            event.preventDefault()
            return
          }
          onSubmit(event)
        }}
      >
        <DialogHeader className="shrink-0 gap-1.5 px-5 py-4 pr-12 text-left">
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div
          data-slot="form-dialog-body"
          className={cn(
            'min-h-0 overflow-y-auto overscroll-contain border-t px-5 py-4',
            bodyClassName,
          )}
        >
          <fieldset
            disabled={busy}
            data-disabled={busy || undefined}
            className="flex min-w-0 flex-col gap-[var(--form-section-gap)] border-0 p-0"
          >
            {children}
          </fieldset>
        </div>

        {error && (
          <div className="shrink-0 border-t px-5 py-3">
            <Alert variant="destructive">
              <AlertCircleIcon aria-hidden="true" />
              <AlertTitle>{errorTitle}</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          </div>
        )}

        <DialogFooter className="shrink-0 border-t bg-surface-subtle px-5 py-3">
          {footer ?? (
            <>
              <DialogClose asChild>
                <Button type="button" variant="outline" disabled={busy}>
                  {cancelLabel}
                </Button>
              </DialogClose>
              <Button type="submit" disabled={busy}>
                {busy && (
                  <LoaderCircleIcon
                    data-icon="inline-start"
                    aria-hidden="true"
                    className="animate-spin"
                  />
                )}
                {busy ? busyLabel : submitLabel}
              </Button>
            </>
          )}
        </DialogFooter>

        <span className="sr-only" role="status" aria-live="polite">
          {busy ? busyLabel : ''}
        </span>
      </form>
    </DialogContent>
  )
}

const FormDialog = Dialog
const FormDialogTrigger = DialogTrigger

export { FormDialog, FormDialogContent, FormDialogTrigger, type FormDialogContentProps }
